import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  Switch,
  message
} from "antd";
import { useTranslation } from "react-i18next";
import { DeleteOutlined } from "@ant-design/icons";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import { errorText } from "../api";
import { BUNDESLAENDER } from "../../../../commons/Payroll/payrollOptions";
import { getPayrollSettings, updatePayrollSettings } from "./api";

const PayrollSettings = () => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [taxOfficeIban, setTaxOfficeIban] = useState("");

  useEffect(() => {
    setLoading(true);
    getPayrollSettings()
      .then(res => {
        form.setFieldsValue(res.data.settings);
        setTaxOfficeIban(res.data.accounting?.taxOfficeIban || "");
      })
      .catch(err => message.error(errorText(err)))
      .finally(() => setLoading(false));
  }, [form]);

  const save = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      const res = await updatePayrollSettings(values);
      form.setFieldsValue(res.data.settings);
      message.success(t("Payroll settings saved"));
    } catch (err) {
      message.error(errorText(err));
    }
    setLoading(false);
  };

  return (
    <Spin spinning={loading}>
      <Form form={form} layout="vertical" onFinish={save}>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card size="small" title={t("Employer")}>
              <Form.Item
                name="betriebsnummer"
                label={t("Betriebsnummer (8 digits)")}
                rules={[{ pattern: /^\d{8}$/, message: t("8 digits") }]}
              >
                <Input maxLength={8} />
              </Form.Item>
              <Row gutter={[12, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item name="steuernummer" label={t("Steuernummer")}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="finanzamt" label={t("Finanzamt")}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[12, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item name="bundesland" label={t("Bundesland")}>
                    <Select allowClear options={BUNDESLAENDER} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="anmeldungInterval" label={t("Lohnsteuer-Anmeldung interval")}>
                    <Select
                      options={[
                        { value: "monthly", label: t("monthly") },
                        { value: "quarterly", label: t("quarterly") },
                        { value: "yearly", label: t("yearly") }
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[12, 0]}>
                <Col xs={12} md={8}>
                  <Form.Item name="umlageU1Rate" label={t("Umlage U1 %")}>
                    <InputNumber min={0} max={10} step={0.1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Item name="umlageU2Rate" label={t("Umlage U2 %")}>
                    <InputNumber min={0} max={10} step={0.1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="payrollDay" label={t("Payroll day")}>
                    <InputNumber min={1} max={28} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="sofortmeldepflichtig"
                label={t("Sofortmeldepflicht (§ 28a Abs. 4 SGB IV, e.g. Baugewerbe)")}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item
                name="elstamEnabled"
                label={t("ELStAM retrieval")}
                valuePropName="checked"
                extra={t(
                  "Retrieving the employees' Lohnsteuerabzugsmerkmale (ELStAM) requires the ELSTER connection of the tax module. Until it is connected, Steuerklasse and Kinderfreibeträge are entered by hand in the employee profile."
                )}
              >
                <Switch />
              </Form.Item>
              <Alert
                type="info"
                showIcon
                message={
                  taxOfficeIban
                    ? `${t("Finanzamt IBAN (from accounting settings)")}: ${taxOfficeIban}`
                    : t(
                        "The Finanzamt IBAN is missing in the accounting settings; the SEPA file cannot include the Lohnsteuer transfer without it."
                      )
                }
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card size="small" title={t("Krankenkassen")}>
              <Form.List name="krankenkassen">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(field => (
                      <Row
                        gutter={[8, 0]}
                        key={field.key}
                        align="middle"
                        style={
                          isMobile
                            ? {
                                borderBottom: "1px solid var(--ds-border-soft)",
                                marginBottom: "var(--ds-space-3)"
                              }
                            : undefined
                        }
                      >
                        <Col xs={24} md={7}>
                          <Form.Item name={[field.name, "name"]} rules={[{ required: true }]}>
                            <Input placeholder={t("Name")} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={5}>
                          <Form.Item
                            name={[field.name, "betriebsnummer"]}
                            rules={[{ pattern: /^\d{8}$/, message: t("8 digits") }]}
                          >
                            <Input placeholder={t("Betriebsnummer")} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={7}>
                          <Form.Item name={[field.name, "iban"]}>
                            <Input placeholder="IBAN" />
                          </Form.Item>
                        </Col>
                        <Col xs={16} sm={12} md={3}>
                          <Form.Item name={[field.name, "bic"]}>
                            <Input placeholder="BIC" />
                          </Form.Item>
                        </Col>
                        <Col xs={8} sm={12} md={2}>
                          <Form.Item>
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              aria-label={t("Delete")}
                              onClick={() => remove(field.name)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    ))}
                    <Button
                      onClick={() => add({ name: "", betriebsnummer: "", iban: "", bic: "" })}
                    >
                      {t("Add Krankenkasse")}
                    </Button>
                  </>
                )}
              </Form.List>
              <Divider />
              <strong>{t("Minijob-Zentrale")}</strong>
              <Row gutter={[8, 0]} style={{ marginTop: 8 }}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name={["minijobZentrale", "betriebsnummer"]}
                    label={t("Betriebsnummer")}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={10}>
                  <Form.Item name={["minijobZentrale", "iban"]} label="IBAN">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name={["minijobZentrale", "bic"]} label="BIC">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card size="small" title={t("Berufsgenossenschaft")}>
              <Row gutter={[12, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item name={["berufsgenossenschaft", "name"]} label={t("Name")}>
                    <Input placeholder="BG BAU" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name={["berufsgenossenschaft", "mitgliedsnummer"]}
                    label={t("Mitgliedsnummer")}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <strong>{t("Gefahrtarifstellen")}</strong>
              <Form.List name={["berufsgenossenschaft", "gefahrtarifstellen"]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(field => (
                      <Row gutter={[8, 0]} key={field.key} style={{ marginTop: 8 }}>
                        <Col xs={8} md={6}>
                          <Form.Item name={[field.name, "code"]} rules={[{ required: true }]}>
                            <Input placeholder={t("Code")} />
                          </Form.Item>
                        </Col>
                        <Col xs={12} md={15}>
                          <Form.Item name={[field.name, "label"]}>
                            <Input placeholder={t("Description")} />
                          </Form.Item>
                        </Col>
                        <Col xs={4} md={3}>
                          <Form.Item>
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              aria-label={t("Delete")}
                              onClick={() => remove(field.name)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    ))}
                    <Button style={{ marginTop: 8 }} onClick={() => add({ code: "", label: "" })}>
                      {t("Add Gefahrtarifstelle")}
                    </Button>
                  </>
                )}
              </Form.List>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card size="small" title="SOKA-BAU">
              <Form.Item
                name={["sokaBau", "member"]}
                label={t("Member of the Urlaubs- und Lohnausgleichskasse (SOKA-BAU)")}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Row gutter={[12, 0]}>
                <Col xs={24} md={8}>
                  <Form.Item name={["sokaBau", "betriebskontoNr"]} label={t("Betriebskonto-Nr.")}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Item name={["sokaBau", "umlageRatePercent"]} label={t("Umlagesatz %")}>
                    <InputNumber min={0} max={100} step={0.1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Item name={["sokaBau", "region"]} label={t("Region")}>
                    <Select
                      options={[
                        { value: "West", label: t("West") },
                        { value: "Ost", label: t("East") }
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Alert
                type="info"
                showIcon
                message={t(
                  "The Umlage is charged on the gross of gewerbliche (hourly) employees flagged as SOKA-BAU in their profile and booked to account 4145."
                )}
              />
            </Card>
          </Col>
        </Row>
        <div className="acct-actions" style={{ marginTop: 16 }}>
          <Button type="primary" htmlType="submit">
            {t("Save")}
          </Button>
        </div>
      </Form>
    </Spin>
  );
};

export default PayrollSettings;
